import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/common/Loading";
import PortalHome from "@/pages/portal/PortalHome";
import PortalJourney from "@/pages/portal/PortalJourney";
import PortalTasks from "@/pages/portal/PortalTasks";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import { SharedWithYou } from "@/components/portal/SharedWithYou";
import PortalBackgroundCheck from "@/pages/portal/PortalBackgroundCheck";
import PortalLicenses, { PortalOffice } from "@/pages/portal/PortalLicenses";
import PortalProjects from "@/pages/portal/PortalPersonal";
import PortalAssociate from "@/pages/portal/PortalAssociate";
import { EventsHub } from "@/pages/events/EventsTraining";
import PortalHelp from "@/pages/portal/PortalHelp";
import PortalOwnership from "@/pages/portal/PortalOwnership";
import { PortalPages } from "@/components/pages/PortalPages";
import { PortalAppointments, PortalScheduling } from "@/pages/portal/PortalAppointments";
import { candidateActionCount } from "@/pages/portal/portalUtils";
import MyTickets from "@/pages/support/MyTickets";
import MyTeam from "@/pages/team/MyTeam";
import { KnowledgeGuides, PortalFaq } from "@/pages/portal/PortalResources";
import { CandidateSupportWidget } from "@/components/support/CandidateSupportWidget";
import { ViewAsBanner } from "@/components/layout/ViewAs";
import { InstallAppDialog } from "@/components/InstallPrompt";
import { Download } from "lucide-react";
import { PortalBell } from "@/components/portal/PortalBell";
import { Link } from "react-router-dom";
import { LogOut, Home, Route as RouteIcon, ClipboardList, CalendarCheck, FolderClosed, BookOpen, LifeBuoy, MoreHorizontal, X, MessageSquare, Users, HelpCircle, FolderKanban, ShieldCheck, BadgeCheck, Building2, CalendarDays, Award, Briefcase, Store } from "lucide-react";

const NAV = [
  { key: "home", label: "Home", Icon: Home },
  { key: "journey", label: "My Journey", Icon: RouteIcon },
  { key: "ownership", label: "Agency Ownership", Icon: Store },
  { key: "associate", label: "Associate Program", Icon: Award },
  { key: "protege", label: "Protégé Program", Icon: Award },
  { key: "events", label: "Events & Training", Icon: CalendarDays },
  { key: "tasks", label: "My Tasks", Icon: ClipboardList },
  { key: "projects", label: "My Projects", Icon: FolderKanban },
  { key: "appointments", label: "Appointments", Icon: CalendarCheck },
  { key: "documents", label: "Documents", Icon: FolderClosed },
  { key: "background", label: "Background Authorization", Icon: ShieldCheck },
  { key: "licenses", label: "Insurance Licenses", Icon: BadgeCheck },
  { key: "office", label: "Office Location", Icon: Building2 },
  { key: "resources", label: "Resources", Icon: BookOpen },
  { key: "support", label: "Support", Icon: LifeBuoy },
];
const MOBILE_PRIMARY = ["home", "projects", "tasks", "events"];

const SUBS = {
  journey: [{ key: "roadmap", label: "My Roadmap" }, { key: "checklist", label: "My Checklist" }],
  appointments: [{ key: "upcoming", label: "My Appointments" }, { key: "book", label: "Book a Meeting" }],
  resources: [{ key: "knowledge", label: "Knowledge & Guides" }, { key: "help", label: "Training & Help" }, { key: "faq", label: "FAQ" }],
};

export default function CandidatePortal() {
  const { user, logout, viewAs } = useAuth();
  const [summary, setSummary] = useState(null);
  const [nav, setNav] = useState(() => {
    const s = sessionStorage.getItem("tr-portal-open");
    sessionStorage.removeItem("tr-portal-open");
    const q = new URLSearchParams(window.location.search);
    if (q.get("task")) return { tab: "tasks", sub: null, taskId: q.get("task") };
    if (q.get("tab") === "tasks") return { tab: "tasks", sub: null };
    try { return s ? JSON.parse(s) : { tab: "home", sub: null }; } catch { return { tab: "home", sub: null }; }
  });
  const [acq, setAcq] = useState(false);
  const [ticketUnread, setTicketUnread] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [assoc, setAssoc] = useState(false);
  const [protege, setProtege] = useState(false);
  const [hasApps, setHasApps] = useState(false);
  const navItems = NAV.filter((n) => (n.key !== "associate" || assoc) && (n.key !== "protege" || protege));
  const goTo = (tab, sub = null, extra = {}) => setNav({ tab, sub: sub || SUBS[tab]?.[0]?.key || null, ...extra });

  useEffect(() => { window.scrollTo(0, 0); }, [nav]);
  useEffect(() => {
    api.get("/support-tickets/my/unread-count").then((r) => setTicketUnread(r.data.unread || 0)).catch(() => {});
    api.get("/portal/tasks").then((r) => setTaskCount(candidateActionCount(r.data.tasks || []))).catch(() => {});
  }, [nav.tab]);
  useEffect(() => {
    api.get("/portal/summary").then((r) => setSummary(r.data)).catch(() => setSummary({}));
    api.get("/associate-bonus/me").then((r) => setAssoc(!!r.data.enrolled)).catch(() => {});
    api.get("/protege-bonus/me").then((r) => setProtege(!!r.data.enrolled)).catch(() => {});
    api.get("/acquisition/me").then((r) => setAcq(!!r.data.applicable)).catch(() => {});
    if (!viewAs) api.get("/applicant/me").then((r) => setHasApps((r.data.applications || []).length > 0)).catch(() => {});
  }, [viewAs]);
  if (!summary) return <div className="min-h-screen flex items-center justify-center"><PageLoader label="Loading your portal" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <ViewAsBanner />
      <header className="bg-[#013A5A] text-white sticky top-0 z-30">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo theme="dark" size={24} />
          <div className="flex items-center gap-3">
            {hasApps && <Link to="/applicant" className="text-sm text-white/90 hover:text-white inline-flex items-center gap-1.5 rounded-md px-2 py-1 hover:bg-white/10" data-testid="portal-applications-link"><Briefcase className="w-4 h-4" />Applications</Link>}
            <span className="hidden sm:block text-sm text-white/80" data-testid="portal-user">{viewAs?.person_name || user?.name}</span>
            {!viewAs && <PortalBell goTo={goTo} />}
            <Button variant="ghost" onClick={logout} data-testid="portal-signout" className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>
      <div className="flex flex-1">
        <SideNav items={navItems} active={nav.tab} goTo={goTo} ticketUnread={ticketUnread} taskCount={taskCount} />
        <main className="flex-1 min-w-0 px-4 sm:px-8 py-8 pb-28 md:pb-12">
          <div className={`${(nav.tab === "journey" && nav.sub === "acquisition") || nav.tab === "ownership" ? "max-w-6xl" : "max-w-5xl"} mx-auto`}><Section nav={nav} goTo={goTo} summary={summary} ticketUnread={ticketUnread} acq={acq} /></div>
        </main>
      </div>
      <BottomNav items={navItems} active={nav.tab} goTo={goTo} ticketUnread={ticketUnread} taskCount={taskCount} logout={logout} />
      <CandidateSupportWidget />
    </div>
  );
}

const Badge = ({ n, testId = "portal-tickets-badge", tone = "bg-rose-500" }) => n > 0 ? <span className={`ml-auto min-w-[18px] h-[18px] px-1 rounded-full ${tone} text-white text-[10px] font-semibold flex items-center justify-center`} data-testid={testId}>{n}</span> : null;

function SideNav({ items, active, goTo, ticketUnread, taskCount }) {
  return (
    <aside className="hidden md:block w-64 shrink-0 border-r border-slate-200 bg-white" data-testid="portal-sidenav">
      <nav className="sticky top-16 p-3 space-y-1">
        {items.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => goTo(key)} data-testid={`portal-nav-${key}`}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active === key ? "bg-[#006090] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
            <Icon className="w-4 h-4" /> {label}
            {key === "support" && <Badge n={ticketUnread} />}
            {key === "tasks" && <Badge n={taskCount} testId="portal-tasks-badge" tone="bg-[#0D9488]" />}
          </button>
        ))}
      </nav>
    </aside>
  );
}

function BottomNav({ items, active, goTo, ticketUnread, taskCount, logout }) {
  const [more, setMore] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const extra = items.filter((n) => !MOBILE_PRIMARY.includes(n.key));
  const go = (k) => { setMore(false); goTo(k); };
  return (
    <div className="md:hidden">
      {more && (
        <div className="fixed inset-x-3 bottom-20 z-40 rounded-2xl bg-white border border-slate-200 shadow-xl p-2" data-testid="portal-mobile-more-menu">
          {extra.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => go(key)} data-testid={`portal-mnav-${key}`} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium ${active === key ? "bg-[#006090]/10 text-[#006090]" : "text-slate-700"}`}>
              <Icon className="w-4 h-4" /> {label}{key === "support" && <Badge n={ticketUnread} />}
            </button>
          ))}
          <button onClick={() => setInstallOpen(true)} data-testid="portal-mnav-install" className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-slate-700"><Download className="w-4 h-4" /> Install App</button>
          <button onClick={logout} data-testid="portal-mnav-signout" className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-rose-600 border-t border-slate-100 mt-1"><LogOut className="w-4 h-4" /> Sign Out</button>
        </div>
      )}
      <InstallAppDialog open={installOpen} onClose={() => setInstallOpen(false)} />
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 grid grid-cols-5" data-testid="portal-bottomnav">
        {items.filter((n) => MOBILE_PRIMARY.includes(n.key)).map(({ key, label, Icon }) => (
          <button key={key} onClick={() => go(key)} data-testid={`portal-bnav-${key}`} className={`relative flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${active === key ? "text-[#006090]" : "text-slate-500"}`}>
            <Icon className="w-5 h-5" />{label.replace("My ", "")}
            {key === "tasks" && taskCount > 0 && <span className="absolute top-1 right-[28%] min-w-[16px] h-4 px-1 rounded-full bg-[#0D9488] text-white text-[9px] font-bold flex items-center justify-center">{taskCount}</span>}
          </button>
        ))}
        <button onClick={() => setMore(!more)} data-testid="portal-bnav-more" className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${extra.some((n) => n.key === active) ? "text-[#006090]" : "text-slate-500"}`}>
          {more ? <X className="w-5 h-5" /> : <MoreHorizontal className="w-5 h-5" />}More
        </button>
      </nav>
    </div>
  );
}

function SubTabs({ tab, sub, goTo, ticketUnread, acq }) {
  const items = SUBS[tab]?.filter((i) => !i.acq || acq);
  if (!items) return null;
  return (
    <div className="flex flex-wrap gap-x-1 border-b border-slate-200 mb-6" data-testid={`portal-subtabs-${tab}`}>
      {items.map((i) => (
        <button key={i.key} onClick={() => goTo(tab, i.key)} data-testid={`portal-subtab-${i.key}`}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${sub === i.key ? "border-[#006090] text-[#006090]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
          {i.label}{i.key === "tickets" && ticketUnread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">{ticketUnread}</span>}
        </button>
      ))}
    </div>
  );
}

const TITLES = { journey: "My Journey", projects: "My Projects", events: "Events & Training", appointments: "Appointments", documents: "Documents", resources: "Resources", support: "Support" };

function SupportOverview({ goTo, openChat, ticketUnread }) {
  const toTeam = () => document.getElementById("portal-support-team")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div className="space-y-8" data-testid="portal-support-overview">
      <p className="text-base text-slate-600 -mt-2">How can we help?</p>
      <SupportQuick goTo={goTo} openChat={openChat} ticketUnread={ticketUnread} toTeam={toTeam} />
      <section id="portal-support-team" data-testid="portal-support-team">
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Your District Team / Who to Contact</h2>
        <MyTeam onMessage={openChat} />
      </section>
      <RecentTickets goTo={goTo} />
    </div>
  );
}

function RecentTickets({ goTo }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/support-tickets/my").then((r) => setRows(r.data.items || [])).catch(() => setRows([])); }, []);
  return (
    <section data-testid="portal-recent-tickets">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">Recent Support Tickets</h2>
        <button onClick={() => goTo("support", "tickets")} className="text-sm font-semibold text-[#006090]" data-testid="portal-tickets-view-all">View All Tickets</button>
      </div>
      {rows === null ? <p className="text-sm text-slate-400">Loading…</p> : rows.length === 0 ? <p className="text-sm text-slate-400" data-testid="portal-recent-tickets-empty">You haven't submitted any support tickets.</p> : (
        <div className="space-y-2">{rows.slice(0, 3).map((t) => (
          <button key={t.id} onClick={() => goTo("support", "tickets")} data-testid={`portal-recent-ticket-${t.id}`} className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3 hover:border-slate-300">
            <span className="min-w-0"><span className="block text-sm font-medium text-slate-800 truncate">{t.subject}</span><span className="text-xs text-slate-500">{t.code} · Updated {new Date(t.updated_at || t.created_at).toLocaleDateString()}</span></span>
            <span className="shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 capitalize">{(t.status || "").replace(/_/g, " ")}</span>
          </button>
        ))}</div>
      )}
    </section>
  );
}

function SupportQuick({ goTo, openChat, ticketUnread, toTeam }) {
  const cards = [
    { k: "chat", Icon: MessageSquare, title: "Message Support", text: "Chat with our support team.", act: openChat },
    { k: "tickets", Icon: LifeBuoy, title: "View My Tickets", text: ticketUnread ? `${ticketUnread} new update(s)` : "Track requests you've submitted.", act: () => goTo("support", "tickets") },
    { k: "team", Icon: Users, title: "Who to Contact", text: "Your recruiter and district team.", act: toTeam },
    { k: "faq", Icon: HelpCircle, title: "Help & FAQ", text: "Answers to common questions.", act: () => goTo("resources", "faq") },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-support-quick">
      {cards.map(({ k, Icon, title, text, act }) => (
        <button key={k} onClick={act} data-testid={`portal-support-quick-${k}`} className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#006090]/40 hover:shadow-sm transition-colors">
          <Icon className="w-5 h-5 text-[#006090]" />
          <p className="mt-2 text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{text}</p>
        </button>
      ))}
    </div>
  );
}

function Section({ nav, goTo, summary, ticketUnread, acq }) {
  const { tab, sub } = nav;
  if (tab === "home") return <PortalHome summary={summary} goTo={goTo} />;
  if (tab === "tasks") return <PortalTasks key={nav.taskId || "tasks"} initialOpenId={nav.taskId} />;
  if (tab === "associate") return <PortalAssociate goTo={goTo} />;
  if (tab === "protege") return <PortalAssociate key="protege" prog="protege" goTo={goTo} />;
  if (tab === "ownership") return <PortalOwnership goTo={goTo} />;
  if (tab === "background") return <PortalBackgroundCheck />;
  if (tab === "licenses") return <PortalLicenses />;
  if (tab === "office") return <PortalOffice />;
  const openChat = () => window.dispatchEvent(new Event("tr-open-support"));
  return (
    <div data-testid={`portal-section-${tab}`}>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{TITLES[tab]}</h1>
      {tab === "support" && sub !== "tickets" && <SupportOverview goTo={goTo} openChat={openChat} ticketUnread={ticketUnread} />}
      <SubTabs tab={tab} sub={sub} goTo={goTo} ticketUnread={ticketUnread} acq={acq} />
      {tab === "journey" && <PortalJourney sub={sub} goTo={goTo} onGoToTeam={() => goTo("support", "team")} />}
      {tab === "appointments" && (sub === "book" ? <PortalScheduling /> : <PortalAppointments onBook={() => goTo("appointments", "book")} />)}
      {tab === "documents" && <><SharedWithYou /><PortalDocuments goTo={goTo} /></>}
      {tab === "projects" && <PortalProjects goTo={goTo} />}
      {tab === "events" && <EventsHub embedded />}
      {tab === "resources" && sub === "knowledge" && <><KnowledgeGuides goTo={goTo} /><PortalPages /></>}
      {tab === "resources" && sub === "faq" && <PortalFaq goTo={goTo} />}
      {tab === "resources" && sub === "help" && <div data-testid="portal-help"><PortalHelp onSubmitTicket={(pre) => goTo("support", "tickets", { ticketNew: true, ticketPrefill: pre || null })} /></div>}
      {tab === "support" && sub === "tickets" && (
        <div data-testid="portal-tickets">
          <button onClick={() => goTo("support")} className="text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="portal-tickets-back">← Back to Support</button>
          <MyTickets key={nav.ticketNew ? "new" : "list"} startNew={!!nav.ticketNew} prefill={nav.ticketPrefill} />
        </div>
      )}
    </div>
  );
}
